import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, CookieWrapper, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import {
  Text,
} from '../../../components';

import { SCREEN_TABLET } from '../../../config/constants';
import { ParagraphTextMedia as Type } from '../../../types/uncinc_profile/paragraphs';
import ImageLightbox from '../../../components/ImageLightbox';

import './style.scss';

interface ParagraphTextMediaProps {
  entity: ImmutableEntity<Type>;
}

const DEFAULT_ASPECT_RATIO = '4_3';
const VIMEO_OEMBED_API = 'https://vimeo.com/api/oembed.json';

const getVimeoWatchUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.hostname === 'player.vimeo.com') {
      const [, videoSegment, videoId] = parsedUrl.pathname.split('/');

      if (videoSegment === 'video' && videoId) {
        const hash = parsedUrl.searchParams.get('h');
        return `https://vimeo.com/${videoId}${hash ? `/${hash}` : ''}`;
      }
    }

    if (parsedUrl.hostname.endsWith('vimeo.com')) {
      return `${parsedUrl.origin}${parsedUrl.pathname}`;
    }
  } catch (error) {
    return url;
  }

  return url;
};

const buildVimeoOembedUrl = (url: string): string => {
  const params = new URLSearchParams({
    url: getVimeoWatchUrl(url),
    controls: 'true',
    responsive: 'true',
    dnt: 'false',
    autoplay: 'false',
    muted: 'false',
    pip: 'true',
    loop: 'false',
  });

  return `${VIMEO_OEMBED_API}?${params.toString()}`;
};

const extractIframeSrc = (html: string): string => {
  const match = html.match(/<iframe[^>]+src="([^"]+)"/i);
  return match?.[1]?.replace(/&amp;/g, '&') ?? '';
};

// TODO: https://uncinc.monday.com/boards/4834198144/pulses/10797556597

const ParagraphTextMedia: React.FC<ParagraphTextMediaProps> = ({
  entity,
}) => {
  const paragraphTextMedia = entity.toJS();
  const { t } = useTranslation();

  const label = paragraphTextMedia.field_label[0]?.value;
  const title = paragraphTextMedia.field_title[0]?.value;
  const body = paragraphTextMedia.field_body[0]?.processed || '';
  const link = paragraphTextMedia.field_link[0];
  const linkTitle = link?.title;
  const linkUri = link?.alias || link?.uri;
  const linkTarget = link?.options?.attributes?.target;
  const secondaryLink = paragraphTextMedia.field_link_secondary[0];
  const secondaryLinkTitle = secondaryLink?.title;
  const secondaryLinkUri = secondaryLink?.alias || secondaryLink?.uri;
  const secondaryLinkTarget = secondaryLink?.options?.attributes?.target;
  const alignment = paragraphTextMedia.field_alignment[0]?.value;
  const media = paragraphTextMedia.field_media_item[0];
  const aspectRatio = paragraphTextMedia.field_aspect_ratio[0]?.value;

  const caption = media.entity.field_caption[0]?.value;
  const embedUrl: string = (media.entity as { field_media_oembed_video?: { value: string }[] }).field_media_oembed_video?.[0]?.value ?? '';
  const isImage = media.bundle === 'image';
  const isVimeo = media.bundle === 'remote_video' && embedUrl?.startsWith('https://vimeo.');

  const { width } = useWindowDimensions();
  const isMobile = width <= SCREEN_TABLET;
  const [mobileVimeoSrc, setMobileVimeoSrc] = React.useState('');

  const vimeoOembedUrl = React.useMemo(() => {
    if (!isVimeo || !embedUrl) {
      return '';
    }

    return buildVimeoOembedUrl(embedUrl);
  }, [embedUrl, isVimeo]);

  React.useEffect(() => {
    if (!isMobile || !isVimeo || !vimeoOembedUrl) {
      setMobileVimeoSrc('');
      return undefined;
    }

    let isMounted = true;

    const fetchVimeoIframe = async () => {
      try {
        const response = await fetch(vimeoOembedUrl);

        if (!response.ok) {
          throw new Error(`Vimeo oEmbed request failed with status ${response.status}`);
        }

        const data: unknown = await response.json();
        const html = typeof data === 'object' && data !== null && 'html' in data && typeof (data as { html?: unknown }).html === 'string'
          ? (data as { html: string }).html
          : '';
        const iframeSrc = extractIframeSrc(html);

        if (isMounted) {
          setMobileVimeoSrc(iframeSrc);
        }
      } catch (error) {
        if (isMounted) {
          setMobileVimeoSrc('');
        }
      }
    };

    fetchVimeoIframe().catch(() => {
      setMobileVimeoSrc('');
    });

    return () => {
      isMounted = false;
    };
  }, [isMobile, isVimeo, vimeoOembedUrl]);

  const renderVimeoPlayer = () => {
    if (!mobileVimeoSrc) {
      return null;
    }

    return (
      <CookieWrapper url={mobileVimeoSrc}>
        <div className="ParagraphTextMedia__vimeo-wrapper">
          <iframe
            src={mobileVimeoSrc}
            frameBorder="0"
            allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            className="ParagraphTextMedia__vimeo-wrapper__iframe"
            title={caption || title || 'Vimeo video player'}
          />
        </div>
      </CookieWrapper>
    );
  };

  const renderMedia = () => {
    if (isVimeo && isMobile) return renderVimeoPlayer();

    const dynamicMedia = (
      <DynamicMediaItem
        media={media}
        caption={caption}
        imageAspectRatio={!aspectRatio ? DEFAULT_ASPECT_RATIO : aspectRatio}
      />
    );

    if (isImage) {
      return (
        <ImageLightbox ariaLabel={t('content_types.article.aria_label')}>
          {dynamicMedia}
        </ImageLightbox>
      );
    }

    return dynamicMedia;
  };

  const alignmentTop = alignment === 'top';
  const alignmentLeft = alignment === 'left';
  const alignmentRight = alignment === 'right';

  const containerClass = classNames('row', {
    'ParagraphTextMedia--flipped': alignmentLeft,
    'ParagraphTextMedia--top': alignmentTop,
  });
  const subClassImage = classNames({
    'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2': alignmentTop,
    'col-12 col-md-6 col-lg-5': alignmentLeft,
    'col-12 col-md-6 col-lg-5 offset-lg-1': alignmentRight,
  });
  const subClassText = classNames({
    'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2': alignmentTop,
    'col-12 col-md-6 offset-lg-1': alignmentLeft,
    'col-12 col-md-6': alignmentRight,
  });

  return (
    <div className="Paragraph ParagraphTextMedia">
      <Container>
        <div className={containerClass}>
          <div className={subClassText}>
            {label && (
              <p className="ParagraphTextMedia__label">
                {label}
              </p>
            )}
            <Text
              label={label}
              title={title}
              text={body}
              link={linkUri}
              linkText={linkTitle}
              buttonClassName="icon-after icon-arrow-right icon-variant-1"
              target={linkTarget}
              secondaryLink={secondaryLinkUri}
              secondaryLinkText={secondaryLinkTitle}
              secondaryTarget={secondaryLinkTarget}
              secondaryButtonClassName="Button--textual icon-after icon-arrow-right"
              secondaryButtonPrimary={false}
            />
          </div>

          <div className={subClassImage}>
            {media && renderMedia()}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphTextMedia;
